import React, { useState, useEffect } from 'react';
import { plansAPI, featuresAPI } from '../../services/api';
import { PLATFORMS } from '../../utils/platformConfig';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { handleApiError } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

const PlanFormModal = ({ plan, onClose, onSuccess }) => {
  const { isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    description: '',
    // New pricing fields
    monthly_price: '',
    yearly_price: '',
    monthly_discount: 0,
    yearly_discount: 0,
    // Legacy fields (for backward compatibility)
    price: '',
    billing_period: 'month',
    // Limits
    max_posts: '',
    max_users: '',
    max_clients: '',
    // Platforms
    platforms: [],
    // Dynamic features (array of feature IDs)
    feature_ids: [],
    // Legacy features (for backward compatibility)
    features: {},
    display_order: 0,
    is_active: true,
  });
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available features
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setLoadingFeatures(true);
        const response = await featuresAPI.getAll();
        setAvailableFeatures(response.data.features || []);
      } catch (err) {
        console.error('Error fetching features:', err);
      } finally {
        setLoadingFeatures(false);
      }
    };
    fetchFeatures();
  }, []);

  // Load plan data when editing
  useEffect(() => {
    if (plan) {
      // Parse platforms
      let platforms = [];
      if (plan.platforms) {
        platforms = Array.isArray(plan.platforms) 
          ? plan.platforms 
          : (typeof plan.platforms === 'string' ? JSON.parse(plan.platforms) : []);
      }

      // Get feature IDs from features array
      const featureIds = (plan.features && Array.isArray(plan.features))
        ? plan.features.map(f => f.id)
        : [];

      setFormData({
        name: plan.name || '',
        name_ar: plan.name_ar || '',
        description: plan.description || '',
        monthly_price: plan.monthly_price !== null && plan.monthly_price !== undefined ? plan.monthly_price : '',
        yearly_price: plan.yearly_price !== null && plan.yearly_price !== undefined ? plan.yearly_price : '',
        monthly_discount: plan.monthly_discount || 0,
        yearly_discount: plan.yearly_discount || 0,
        price: plan.price || '', // Legacy
        billing_period: plan.billing_period || 'month', // Legacy
        max_posts: plan.max_posts !== null && plan.max_posts !== undefined ? plan.max_posts : '',
        max_users: plan.max_users !== null && plan.max_users !== undefined ? plan.max_users : '',
        max_clients: plan.max_clients !== null && plan.max_clients !== undefined ? plan.max_clients : '',
        platforms: platforms,
        feature_ids: featureIds,
        features: plan.features_legacy || plan.features || {}, // Legacy
        display_order: plan.display_order || 0,
        is_active: plan.is_active !== false,
      });
    }
  }, [plan]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : parseFloat(value)) : value,
    });
    setError(null);
  };

  const handlePlatformChange = (platformId) => {
    const currentPlatforms = formData.platforms || [];
    if (currentPlatforms.includes(platformId)) {
      setFormData({
        ...formData,
        platforms: currentPlatforms.filter(p => p !== platformId),
      });
    } else {
      setFormData({
        ...formData,
        platforms: [...currentPlatforms, platformId],
      });
    }
  };

  const handleSelectAllPlatforms = () => {
    const allPlatformIds = Object.keys(PLATFORMS);
    if (formData.platforms.length === allPlatformIds.length) {
      setFormData({
        ...formData,
        platforms: [],
      });
    } else {
      setFormData({
        ...formData,
        platforms: [...allPlatformIds],
      });
    }
  };

  const handleFeatureChange = (featureId) => {
    const currentFeatures = formData.feature_ids || [];
    if (currentFeatures.includes(featureId)) {
      setFormData({
        ...formData,
        feature_ids: currentFeatures.filter(id => id !== featureId),
      });
    } else {
      setFormData({
        ...formData,
        feature_ids: [...currentFeatures, featureId],
      });
    }
  };

  const calculateFinalPrice = (price, discount) => {
    if (!price || price === 0) return 0;
    if (!discount || discount === 0) return price;
    return Math.round(price * (1 - discount / 100) * 100) / 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation: Must have at least monthly_price or yearly_price
      if (!formData.monthly_price && !formData.yearly_price && !formData.price) {
        throw new Error('At least monthly price or yearly price is required');
      }

      const submitData = {
        name: formData.name,
        name_ar: formData.name_ar || null,
        description: formData.description || null,
        monthly_price: formData.monthly_price ? parseFloat(formData.monthly_price) : null,
        yearly_price: formData.yearly_price ? parseFloat(formData.yearly_price) : null,
        monthly_discount: parseFloat(formData.monthly_discount) || 0,
        yearly_discount: parseFloat(formData.yearly_discount) || 0,
        price: formData.price ? parseFloat(formData.price) : null, // Legacy
        billing_period: formData.billing_period, // Legacy
        max_posts: formData.max_posts === '' ? null : parseInt(formData.max_posts),
        max_users: formData.max_users === '' ? null : parseInt(formData.max_users),
        max_clients: formData.max_clients === '' ? null : parseInt(formData.max_clients),
        platforms: formData.platforms || [],
        feature_ids: formData.feature_ids || [],
        features: formData.features || {}, // Legacy
        display_order: parseInt(formData.display_order) || 0,
        is_active: formData.is_active,
      };

      if (plan) {
        await plansAPI.update(plan.id, submitData);
      } else {
        await plansAPI.create(submitData);
      }
      onSuccess();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const finalMonthlyPrice = formData.monthly_price 
    ? calculateFinalPrice(parseFloat(formData.monthly_price), parseFloat(formData.monthly_discount))
    : null;
  const finalYearlyPrice = formData.yearly_price 
    ? calculateFinalPrice(parseFloat(formData.yearly_price), parseFloat(formData.yearly_discount))
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {plan ? 'Edit Plan' : 'Add New Plan'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Name (English) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Basic, Professional"
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="name_ar" className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Name (Arabic)
                </label>
                <input
                  type="text"
                  id="name_ar"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  placeholder="مثل: أساسي، احترافي"
                  className="input w-full"
                  dir="rtl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  id="display_order"
                  name="display_order"
                  min="0"
                  value={formData.display_order}
                  onChange={handleChange}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the plan features and benefits..."
                className="textarea w-full"
              />
            </div>

            {/* Pricing Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Monthly Price */}
                <div>
                  <label htmlFor="monthly_price" className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Price ($)
                  </label>
                  <input
                    type="number"
                    id="monthly_price"
                    name="monthly_price"
                    min="0"
                    step="0.01"
                    value={formData.monthly_price}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="input w-full"
                  />
                  {formData.monthly_price && (
                    <div className="mt-2">
                      <label htmlFor="monthly_discount" className="block text-xs text-gray-600 mb-1">
                        Monthly Discount (%)
                      </label>
                      <input
                        type="number"
                        id="monthly_discount"
                        name="monthly_discount"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.monthly_discount}
                        onChange={handleChange}
                        className="input w-full text-sm"
                      />
                      {finalMonthlyPrice !== null && (
                        <p className="text-xs text-green-600 mt-1">
                          Final Price: ${finalMonthlyPrice.toFixed(2)}/month
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Yearly Price */}
                <div>
                  <label htmlFor="yearly_price" className="block text-sm font-medium text-gray-700 mb-2">
                    Yearly Price ($)
                  </label>
                  <input
                    type="number"
                    id="yearly_price"
                    name="yearly_price"
                    min="0"
                    step="0.01"
                    value={formData.yearly_price}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="input w-full"
                  />
                  {formData.yearly_price && (
                    <div className="mt-2">
                      <label htmlFor="yearly_discount" className="block text-xs text-gray-600 mb-1">
                        Yearly Discount (%)
                      </label>
                      <input
                        type="number"
                        id="yearly_discount"
                        name="yearly_discount"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.yearly_discount}
                        onChange={handleChange}
                        className="input w-full text-sm"
                      />
                      {finalYearlyPrice !== null && (
                        <p className="text-xs text-green-600 mt-1">
                          Final Price: ${finalYearlyPrice.toFixed(2)}/year
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Limits */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Limits (leave empty for unlimited)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="max_posts" className="block text-sm font-medium text-gray-700 mb-2">
                    Max Posts
                  </label>
                  <input
                    type="number"
                    id="max_posts"
                    name="max_posts"
                    min="0"
                    value={formData.max_posts}
                    onChange={handleChange}
                    placeholder="Leave empty for unlimited"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label htmlFor="max_users" className="block text-sm font-medium text-gray-700 mb-2">
                    Max Users
                  </label>
                  <input
                    type="number"
                    id="max_users"
                    name="max_users"
                    min="0"
                    value={formData.max_users}
                    onChange={handleChange}
                    placeholder="Leave empty for unlimited"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label htmlFor="max_clients" className="block text-sm font-medium text-gray-700 mb-2">
                    Max Clients
                  </label>
                  <input
                    type="number"
                    id="max_clients"
                    name="max_clients"
                    min="0"
                    value={formData.max_clients}
                    onChange={handleChange}
                    placeholder="Leave empty for unlimited"
                    className="input w-full"
                  />
                </div>
              </div>
            </div>

            {/* Platforms */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">Platforms</h3>
                <button
                  type="button"
                  onClick={handleSelectAllPlatforms}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {formData.platforms.length === Object.keys(PLATFORMS).length 
                    ? 'Deselect All' 
                    : 'Select All'}
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Select platforms included in this plan. Leave empty for all platforms.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.values(PLATFORMS).map((platform) => (
                  <label
                    key={platform.id}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.platforms.includes(platform.id)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes(platform.id)}
                      onChange={() => handlePlatformChange(platform.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className={`ml-2 text-sm ${isRTL ? 'mr-2 ml-0' : ''}`}>
                      {platform.icon} {platform.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Dynamic Features */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Features</h3>
              {loadingFeatures ? (
                <Loading />
              ) : availableFeatures.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No features available. Please add features first.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availableFeatures.map((feature) => (
                    <label
                      key={feature.id}
                      className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.feature_ids.includes(feature.id)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.feature_ids.includes(feature.id)}
                        onChange={() => handleFeatureChange(feature.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                      />
                      <div className={`ml-3 ${isRTL ? 'mr-3 ml-0' : ''}`}>
                        <div className="flex items-center">
                          {feature.icon && <span className="mr-2">{feature.icon}</span>}
                          <span className="text-sm font-medium text-gray-900">
                            {feature.name}
                          </span>
                        </div>
                        {feature.description && (
                          <p className="text-xs text-gray-500 mt-1">{feature.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Active Status */}
            {plan && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className={`ml-2 block text-sm text-gray-700 ${isRTL ? 'mr-2 ml-0' : ''}`}>
                  Plan is active (visible to users)
                </label>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlanFormModal;
